(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const text = {
    added: 'Added to cart',
    failed: 'Could not add this item. Please try again.',
    unavailable: 'This option is unavailable.',
    copied: 'Link copied',
    copyFailed: 'Copy the link from your browser.'
  };

  const announce = (element, message) => {
    if (!element) return;
    element.textContent = message;
    element.removeAttribute('hidden');
  };

  const initTrustTabs = () => {
    document.querySelectorAll('[data-mirabase-trust-tabs]').forEach((group) => {
      const tabs = [...group.querySelectorAll('[data-mirabase-trust-tab]')];
      const panels = [...group.querySelectorAll('[data-mirabase-trust-panel]')];
      if (!tabs.length || !panels.length) return;
      const setActive = (name) => {
        tabs.forEach((tab) => {
          const active = tab.dataset.mirabaseTrustTab === name;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
        });
        panels.forEach((panel) => {
          panel.hidden = panel.dataset.mirabaseTrustPanel !== name;
        });
      };
      tabs.forEach((tab) => tab.addEventListener('click', () => setActive(tab.dataset.mirabaseTrustTab)));
      setActive(tabs[0].dataset.mirabaseTrustTab);
    });
  };

  const initCollectionFilters = () => {
    document.querySelectorAll('[data-mirabase-filter-group]').forEach((group) => {
      /* Catalog / scenarios / highlights filters are owned by mb-interactions.js */
      if (
        group.hasAttribute('data-mb-scenarios') ||
        group.hasAttribute('data-mb-highlights') ||
        group.hasAttribute('data-mb-catalog-tag') ||
        group.classList.contains('mb-scenarios') ||
        group.classList.contains('mb-catalog')
      ) {
        return;
      }
      const collection = group.closest('.mirabase-collection') || document;
      const cards = [...collection.querySelectorAll('[data-mirabase-product-card]')];
      const controls = [...group.querySelectorAll('select[data-mirabase-filter]')];
      const count = group.querySelector('[data-mirabase-filter-count]');
      const empty = group.querySelector('[data-mirabase-empty]');
      const clear = group.querySelector('[data-mirabase-filter-clear]');
      if (!controls.length || !cards.length) return;
      const apply = () => {
        const values = {};
        controls.forEach((control) => { values[control.dataset.mirabaseFilter] = control.value; });
        let visible = 0;
        cards.forEach((card) => {
          const categoryMatch = !values.category || values.category === 'all' || card.dataset.category === values.category;
          const sceneMatch = !values.scene || values.scene === 'all' || card.dataset.scene === values.scene;
          const show = categoryMatch && sceneMatch;
          card.hidden = !show;
          if (show) visible += 1;
        });
        if (count) {
          const template = count.dataset.countTemplate;
          if (template) count.textContent = template.replace('__COUNT__', String(visible));
          else count.textContent = `${visible} products`;
        }
        if (empty) empty.hidden = visible !== 0;
      };
      controls.forEach((control) => control.addEventListener('change', apply));
      if (clear) {
        clear.addEventListener('click', () => {
          controls.forEach((control) => { control.value = 'all'; });
          apply();
        });
      }
      apply();
    });
  };

  const initGallery = () => {
    document.querySelectorAll('[data-mirabase-product-root]').forEach((root) => {
      const main = root.querySelector('[data-mirabase-main-media]');
      const mainImg = main?.querySelector('img');
      const thumbs = [...root.querySelectorAll('[data-mirabase-media-thumb]')];
      if (!main || !mainImg || !thumbs.length) return;
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const src = thumb.dataset.src;
          if (src) {
            mainImg.src = src;
            mainImg.removeAttribute('srcset');
            mainImg.removeAttribute('sizes');
          }
          if (thumb.dataset.alt) mainImg.alt = thumb.dataset.alt;
          thumbs.forEach((item) => item.classList.toggle('is-active', item === thumb));
          mainImg.classList.remove('is-loaded');
          if (mainImg.complete) mainImg.classList.add('is-loaded');
        });
      });
    });
  };

  const initStickyBuy = () => {
    document.querySelectorAll('[data-mirabase-sticky-buy]').forEach((bar) => {
      const root = document.querySelector('[data-mirabase-product-root]');
      const form = root?.querySelector('#mirabase-product-form');
      const buyRow = root?.querySelector('.mirabase-product__buy-row');
      if (!root || !form || !buyRow) return;
      const show = () => {
        const rect = buyRow.getBoundingClientRect();
        const visible = rect.bottom < 0;
        bar.hidden = !visible;
      };
      show();
      window.addEventListener('scroll', show, { passive: true });
      bar.querySelector('[data-mirabase-sticky-submit]')?.addEventListener('click', () => form.requestSubmit());
    });
  };

  const initProductForm = () => {
    document.querySelectorAll('[data-mirabase-product-root]').forEach((root) => {
      const form = root.querySelector('#mirabase-product-form');
      const variant = root.querySelector('[data-mirabase-variant]');
      const submit = root.querySelector('[data-mirabase-submit]');
      const sticky = root.querySelector('[data-mirabase-sticky-submit]');
      const status = root.querySelector('[data-mirabase-product-status]');
      const price = root.querySelector('[data-mirabase-price]');
      if (!form || !variant) return;
      const refresh = () => {
        const selected = variant.options[variant.selectedIndex];
        if (!selected) return;
        if (price && selected.dataset.price) price.textContent = selected.dataset.price;
        const disabled = selected.disabled;
        if (submit) {
          submit.disabled = disabled;
          submit.setAttribute('aria-disabled', String(disabled));
        }
        if (sticky) {
          sticky.disabled = disabled;
          sticky.setAttribute('aria-disabled', String(disabled));
        }
        if (disabled) announce(status, text.unavailable);
        else if (status) status.hidden = true;
      };
      variant.addEventListener('change', refresh);
      refresh();
      const add = async (event) => {
        event.preventDefault();
        if (submit?.disabled) return;
        if (!form.dataset.ajax) {
          form.submit();
          return;
        }
        const button = submit;
        if (button) button.disabled = true;
        announce(status, 'Adding...');
        try {
          const response = await fetch(form.action || '/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          if (!response.ok) throw new Error('request failed');
          document.dispatchEvent(new CustomEvent('mirabase:cart-added', { detail: await response.json() }));
          announce(status, text.added);
          if (window.Shopify && window.Shopify.theme && window.Shopify.theme.cartDrawer) {
            window.Shopify.theme.cartDrawer.open();
          }
        } catch (error) {
          announce(status, text.failed);
        } finally {
          refresh();
        }
      };
      form.addEventListener('submit', add);
      if (sticky) sticky.addEventListener('click', () => form.requestSubmit());
    });
  };

  const initCopy = () => {
    document.querySelectorAll('[data-mirabase-copy-link]').forEach((button) => {
      button.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(window.location.href);
          announce(button, text.copied);
        } catch (error) {
          announce(button, text.copyFailed);
        }
      });
    });
  };

  const initRail = () => {
    document.querySelectorAll('[data-mirabase-ugc-rail]').forEach((rail) => {
      const track = rail.querySelector('[data-mirabase-ugc-track]');
      if (!track) return;
      rail.querySelector('[data-mirabase-ugc-prev]')?.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth * 0.8, behavior: reducedMotion ? 'auto' : 'smooth' }));
      rail.querySelector('[data-mirabase-ugc-next]')?.addEventListener('click', () => track.scrollBy({ left: track.clientWidth * 0.8, behavior: reducedMotion ? 'auto' : 'smooth' }));
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    initTrustTabs();
    initCollectionFilters();
    initGallery();
    initStickyBuy();
    initProductForm();
    initCopy();
    initRail();
  });
})();
